/* global globalThis */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  useMediaQuery,
  GlobalStyles,
  Popover,
  MenuItem,
  TextField,
  InputAdornment,
  Checkbox,
  Radio,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import ProgramIcon from "../dashboard/ProgramIcon";
import CommonSelect from "../common/CommonSelect";
import { useDispatch, useSelector } from "react-redux";
import { toggleCgPlus, setCgPlus } from "../../redux/slices/togglesSlice";
import ToggleButton from "../common/toggleButton/toggleButton";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs from "dayjs";
import {
  fetchCagFilterOptions,
  getCagConfiguration,
  fetchCagGroupsSearch,
} from "../../api/cagConfigurations";
import { fetchCarriers } from "../../api/filterType";
import { useLoading } from "../../context/LoadingContext";
import { formatDateOnly } from "../../utils/formatDateTime";

const ORG_OPTIONS = [];
const CARRIER_OPTIONS = [];
const CARRIER_NAME_OPTIONS = [];
const GROUP_ID_OPTIONS = [];
const GROUP_NAME_OPTIONS = [];
const STATUS_OPTIONS = ["Yes", "No"];
const PROGRAM_DRUG_LIST_MAP = {
  "GLP-1": ["ECM", "LRX_ASSIST", "LUCYRX_WEIGHT MGMT", "TOP TIER EXPANDED"],
  Biosimilars: ["TOP TIER EXPANDED", "Orencia"],
  ECMP: ["ECM"],
  PAP: ["LRX_ASSIST_NON SPECIALTY"],
  "Women's Health Program": ["Abiraterone Acetate"],
  "International Sourcing": ["ECM", "LUCYRX_WEIGHT MGMT", "TOP TIER EXPANDED"],
  "Copay Assist": ["LRX_ASSIST", "ECM", "Orencia"],
};

const PROGRAMS = Object.keys(PROGRAM_DRUG_LIST_MAP);

const VENDOR_OPTIONS = ["GRM"];

const createInitialGroupDetails = () => ({
  org: "",
  orgName: "",
  carrierId: "",
  carrierName: "",
  groupId: "",
  groupName: "",
  activeLives: "0",
  groupStartDate: "",
  groupEndDate: "",
  pcaDirectiveEnabled: false,
  pcaDirective: "",
  parentAccountName: "",
});

const mapProgramConfig = (pc) => {
  const isEnabled = pc.status;
  const defaultVendor =
    pc.programName === "International Sourcing" ||
    pc.programName === "Copay Assist"
      ? ""
      : "-";

  let drugList = [];
  let vendor = defaultVendor;

  if (isEnabled) {
    drugList = (pc.drugListConfigs || [])
      .filter((d) => d.enabled)
      .map((d) => d.drugListName);

    const activeVendors = (pc.vendorConfigs || []).filter((v) => v.enabled);
    vendor =
      activeVendors.length > 0 ? activeVendors[0].vendorName : defaultVendor;
  }

  return {
    programId: pc.programId,
    program: pc.programName,
    status: isEnabled ? "Yes" : "No",
    drugList,
    vendor,
    originalPc: pc,
  };
};

const isProgramCgPlus = (config) => {
  if (!config) return false;
  if (config.originalPc && typeof config.originalPc.isCgPlus === "boolean") {
    return config.originalPc.isCgPlus;
  }
  if (typeof config.isCgPlus === "boolean") {
    return config.isCgPlus;
  }
  const name = String(config.program || config.programName || config.name || "")
    .trim()
    .toLowerCase();
  return name === "glp-1" || name === "glp1" || name.includes("women");
};

const isProgramActive = (config) => {
  if (!config) return false;
  if (typeof config.status === "boolean") return config.status;
  if (typeof config.enabled === "boolean") return config.enabled;
  if (config.originalPc) {
    if (typeof config.originalPc.status === "boolean") {
      return config.originalPc.status;
    }
    if (typeof config.originalPc.enabled === "boolean") {
      return config.originalPc.enabled;
    }
  }
  const statusStr = String(config.status || "")
    .trim()
    .toUpperCase();
  return statusStr === "YES" || statusStr === "TRUE" || statusStr === "ACTIVE";
};

const isOnlyCgPlusActive = (programsList) => {
  if (!Array.isArray(programsList) || programsList.length === 0) return false;
  const activePrograms = programsList.filter(isProgramActive);
  if (activePrograms.length === 0) return false;
  return activePrograms.every(isProgramCgPlus);
};

const fetchOptionsForField = async (field, filters, hierarchy) => {
  if (field === "org") {
    return (hierarchy || []).map((org) => ({
      id: org.orgCode,
      displayValue: org.orgName,
    }));
  }
  if (field === "carrier") {
    const selectedOrg = filters.org?.[0];
    const orgData = (hierarchy || []).find((o) => o.orgCode === selectedOrg);
    if (orgData) {
      return orgData.carriers.map((c) => ({
        id: c.carrierCode,
        displayValue: c.carrierName,
      }));
    }
    const res = await fetchCarriers({
      outreachType: "MEMBER_OUTREACH",
      ...filters,
    });
    return res.map((c) => ({ id: c.carrierCode, displayValue: c.carrierName }));
  }
  if (field === "group") {
    const payload = { carrierCodes: filters.carrier || [], searchTerm: "" };
    const res = await fetchCagGroupsSearch(payload);
    const raw = res?.data?.content || res?.data || [];
    return raw.map((g) => ({
      id: g.id || g.groupNumber,
      displayValue: g.displayValue || g.groupName,
      activeLives: g.activeLives || 0,
      groupStartDate: g.groupStartDate,
      groupEndDate: g.groupEndDate,
    }));
  }
  const res = await fetchCagFilterOptions({ field, searchTerm: "", filters });
  return res.data?.content || [];
};

const createComparableProgramConfig = (programs = []) =>
  programs.map((program) => ({
    programId: program.programId ?? null,
    program: program.program ?? "",
    status: program.status ?? "No",
    drugList: Array.isArray(program.drugList) ? [...program.drugList] : [],
    vendor: program.vendor ?? "-",
  }));

const createComparableModalState = (groupDetails, programConfig) => ({
  groupDetails: {
    org: groupDetails?.org ?? "",
    orgName: groupDetails?.orgName ?? "",
    carrierId: groupDetails?.carrierId ?? "",
    carrierName: groupDetails?.carrierName ?? "",
    groupId: groupDetails?.groupId ?? "",
    groupName: groupDetails?.groupName ?? "",
    activeLives: String(groupDetails?.activeLives ?? "0"),
    groupStartDate: groupDetails?.groupStartDate ?? "",
    groupEndDate: groupDetails?.groupEndDate ?? "",
    careGuideEffectiveDate: groupDetails?.careGuideEffectiveDate ?? "",
    pcaDirectiveEnabled: Boolean(groupDetails?.pcaDirectiveEnabled),
    pcaDirective: groupDetails?.pcaDirective ?? "",
    parentAccountName: groupDetails?.parentAccountName ?? "",
  },
  programConfig: createComparableProgramConfig(programConfig),
});

const FilterDropdown = ({
  label,
  options,
  selected,
  onChange,
  placeholder,
  multiple = false,
  width = "16.9rem",
  popoverWidth,
  sx = {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleLimit, setVisibleLimit] = useState(100);
  const [resolvedPopoverWidth, setResolvedPopoverWidth] = useState(
    popoverWidth || width,
  );
  const triggerRef = useRef(null);
  const open = Boolean(anchorEl);

  // Reset visible limit when search or open state changes
  useEffect(() => {
    setVisibleLimit(100);
  }, [searchTerm, open]);

  const getOptionValue = (opt) =>
    opt && typeof opt === "object" ? opt.value : opt;
  const getOptionLabel = (opt) =>
    opt && typeof opt === "object" ? opt.label : opt;

  const filterPredicate = (opt) =>
    String(getOptionLabel(opt))
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

  const filteredOptions = (options || []).filter(filterPredicate);

  const handleSingleSelect = (opt) => {
    onChange(getOptionValue(opt));
    setAnchorEl(null);
  };

  const handleMultipleSelect = (opt) => {
    const val = getOptionValue(opt);
    const currentSelected = selected || [];
    const isSelected = currentSelected.some((s) => String(s) === String(val));
    if (isSelected) {
      onChange(currentSelected.filter((s) => String(s) !== String(val)));
    } else {
      onChange([...currentSelected, val]);
    }
  };

  const handleDropdownToggle = (event) => {
    if (sx?.cursor === "not-allowed") return;

    event.preventDefault();
    event.stopPropagation();

    if (open) {
      setAnchorEl(null);
      return;
    }

    const nextAnchorEl = event.currentTarget;
    triggerRef.current = nextAnchorEl;
    setResolvedPopoverWidth(popoverWidth || nextAnchorEl?.clientWidth || width);
    setAnchorEl(nextAnchorEl);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      if (visibleLimit < filteredOptions.length) {
        setVisibleLimit((prev) => prev + 100);
      }
    }
  };

  const renderValue = () => {
    const currentSelected = selected || [];
    if (multiple) {
      if (currentSelected.length > 0) {
        return (
          <Typography
            sx={{ color: "#10100F", fontSize: "14px", fontWeight: 500 }}
          >
            {currentSelected.length} Selected
          </Typography>
        );
      }
      return (
        <Typography
          sx={{ color: "#999893", fontSize: "13px", fontWeight: 500 }}
        >
          {placeholder || `Select ${label}`}
        </Typography>
      );
    }

    // Single select display logic
    let displayValue = "";
    if (selected !== undefined && selected !== null && selected !== "") {
      const selectedOpt = (options || []).find(
        (opt) => String(getOptionValue(opt)) === String(selected),
      );
      displayValue = selectedOpt ? getOptionLabel(selectedOpt) : selected;
    } else {
      displayValue = placeholder;
    }

    const isTruncated = displayValue && String(displayValue).length > 19;
    const finalDisplay = isTruncated
      ? String(displayValue).substring(0, 19) + "..."
      : displayValue;

    return (
      <Typography
        sx={{
          color: selected ? "#10100F" : "#999893",
          fontWeight: 500,
          fontSize: "13.5px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {finalDisplay}
      </Typography>
    );
  };

  return (
    <Box sx={{ width }}>
      <Box
        onMouseDown={handleDropdownToggle}
        sx={{
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          px: "16px",
          gap: "10px",
          height: "32px !important",
          background: "#FFFFFF",
          border: "1px solid #E9E9E8",
          borderRadius: "8px",
          cursor: "pointer",
          ...sx,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexWrap: "nowrap",
            gap: "4px",
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          {renderValue()}
        </Box>
        {open ? (
          <CloseIcon sx={{ color: "#6E6D92", fontSize: "18px" }} />
        ) : (
          <ExpandMoreIcon sx={{ color: "#6E6D92" }} />
        )}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableScrollLock
        disableRestoreFocus
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              bgcolor: "#6E6D92",
              color: "#fff",
              borderRadius: "10px",
              overflow: "hidden",
              boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
              minWidth: resolvedPopoverWidth,
              maxWidth: resolvedPopoverWidth,
            },
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Box sx={{ px: 0.5, pb: 1, pt: 0.5 }}>
            <TextField
              placeholder="Search..."
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              onClick={(e) => e.stopPropagation()}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#fff",
                  height: "38px",
                  fontSize: "15px",
                  color: "#1B2F3F",
                  borderRadius: "6px",
                  "& fieldset": { borderColor: "#E8E8E9" },
                  "&:hover fieldset": { borderColor: "#E8E8E9" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#E8E8E9",
                    borderWidth: "1px",
                  },
                },
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon sx={{ color: "#92A0B3", fontSize: "20px" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          <Box
            onScroll={handleScroll}
            sx={{
              maxHeight: 250,
              overflowY: "auto",
              px: 0.5,
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(255,255,255,0.3) transparent",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255,255,255,0.3)",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(255,255,255,0.5)",
              },
            }}
          >
            {filteredOptions.length === 0 && (
              <Typography
                sx={{
                  textAlign: "center",
                  p: 2,
                  fontSize: "13px",
                  opacity: 0.7,
                }}
              >
                No results found
              </Typography>
            )}

            {filteredOptions.slice(0, visibleLimit).map((opt, idx) => {
              const optValue = getOptionValue(opt);
              const isSelected = multiple
                ? selected?.some((s) => String(s) === String(optValue))
                : String(selected) === String(optValue);

              return (
                <MenuItem
                  key={`${optValue}-${idx}`}
                  onClick={() =>
                    multiple
                      ? handleMultipleSelect(opt)
                      : handleSingleSelect(opt)
                  }
                  sx={{
                    minHeight: 34,
                    height: 38,
                    justifyContent: "space-between",
                    bgcolor: isSelected
                      ? "rgba(255,255,255,0.12)"
                      : "transparent",
                    borderRadius: "4px",
                    px: "12px",
                    mb:
                      idx === filteredOptions.slice(0, visibleLimit).length - 1
                        ? 0
                        : "2px",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                    transition: "background-color 0.2s",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Tooltip
                    title={getOptionLabel(opt)}
                    placement="top-start"
                    arrow
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#fff",
                        fontWeight: isSelected ? 600 : 400,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        flex: 1,
                      }}
                    >
                      {getOptionLabel(opt)}
                    </Typography>
                  </Tooltip>
                  {multiple ? (
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      sx={{
                        color: "rgba(255,255,255,0.7)",
                        "&.Mui-checked": { color: "#fff" },
                        p: 0,
                      }}
                    />
                  ) : (
                    <Radio
                      checked={isSelected}
                      size="small"
                      icon={
                        <RadioButtonUncheckedIcon
                          sx={{
                            color: "rgba(255,255,255,0.7)",
                            fontSize: 18,
                          }}
                        />
                      }
                      checkedIcon={
                        <RadioButtonCheckedIcon
                          sx={{ color: "#fff", fontSize: 18 }}
                        />
                      }
                      sx={{ p: 0 }}
                    />
                  )}
                </MenuItem>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

FilterDropdown.propTypes = {
  label: PropTypes.string,
  options: PropTypes.array,
  selected: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  popoverWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sx: PropTypes.object,
};

const formatDateForBackend = (val) => {
  if (!val) return null;
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (!trimmed) return null;
    if (trimmed.includes("T")) return trimmed.split("T")[0];
    if (trimmed.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      return trimmed.slice(0, 10);
    }
  }
  const d = dayjs(val);
  return d.isValid() ? d.format("YYYY-MM-DD") : val;
};

const determineReadonly = (item, initialData, groupDetails) => {
  const isPcaField = item.field === "pcaDirective";
  const isParentAccountField = item.field === "parentAccountName";
  const isCareGuideEffectiveDateField = item.field === "careGuideEffectiveDate";
  const isEditableField =
    isPcaField || isParentAccountField || isCareGuideEffectiveDateField;

  if (
    (initialData && !isEditableField) ||
    item.field === "activeLives" ||
    item.field === "groupStartDate" ||
    item.field === "groupEndDate"
  )
    return true;
  const isCarrierField =
    item.field === "carrierName" || item.field === "carrierId";
  if (isCarrierField && !groupDetails.org) return true;
  const isGroupField = item.field === "groupName" || item.field === "groupId";
  if (isGroupField && (!groupDetails.carrierName || !groupDetails.carrierId))
    return true;

  if (
    isPcaField &&
    (!groupDetails.org || !groupDetails.carrierId || !groupDetails.groupId)
  )
    return true;

  return false;
};

const getGroupDetailOptions = (
  label,
  defaultOptions,
  dynamicOrg,
  dynamicCarrier,
  dynamicGroup,
) => {
  switch (label) {
    case "ORG":
      return dynamicOrg.map((opt) => ({
        value: opt.id,
        label: opt.displayValue,
      }));
    case "Carrier Name":
      return dynamicCarrier.map((opt) => ({
        value: opt.id,
        label: opt.displayValue,
      }));
    case "Carrier ID":
      return dynamicCarrier.map((opt) => ({ value: opt.id, label: opt.id }));
    case "Group Name":
      return dynamicGroup.map((opt) => ({
        value: opt.id,
        label: String(opt.displayValue),
      }));
    case "Group Number":
      return dynamicGroup.map((opt) => ({
        value: opt.id,
        label: String(opt.id),
      }));
    default:
      return defaultOptions || [];
  }
};

const InputContent = ({
  isReadonly,
  item,
  groupDetails,
  contentEditableRef,
  handlePcaInput,
  handlePcaPaste,
  handlePcaDrop,
  setIsFocused,
  handleGroupDetailsChange,
}) => {
  if (isReadonly) {
    let val = item.displayValue || groupDetails[item.field] || "";
    if (item.isDate && val) {
      val = formatDateOnly(val);
    }
    if (!val) val = "-";
    const displayVal =
      String(val).length > 22 ? String(val).substring(0, 22) + "..." : val;
    return (
      <Typography
        sx={{
          width: "100%",
          fontWeight: 500,
          fontSize: "13.5px",
          color: "#6B6862",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          cursor: "not-allowed",
        }}
      >
        {displayVal}
      </Typography>
    );
  }

  if (item.type === "textarea") {
    return (
      <Box
        contentEditable
        ref={contentEditableRef}
        suppressContentEditableWarning
        onInput={handlePcaInput}
        onPaste={handlePcaPaste}
        onDrop={handlePcaDrop}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={item.placeholder}
        sx={{
          minHeight: "60px",
          maxHeight: "80px",
          padding: "5px 7px",
          backgroundColor: "#FFFFFF",
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
          outline: "none",
          overflowY: "auto",
          wordBreak: "break-word",
          fontWeight: 500,
          fontSize: "13.5px",
          color: "#221E15",
          lineHeight: 1.5,
          cursor: "text",
          userSelect: "auto",
          opacity: 1,
          pointerEvents: "auto",
          "&:focus": { borderColor: "#403C6F" },
          "&:empty:before": {
            content: "attr(placeholder)",
            color: "#999893",
            pointerEvents: "none",
            display: "block",
          },
        }}
      />
    );
  }

  if (item.isDate) {
    const rawVal = item.displayValue || groupDetails[item.field] || null;
    const dayjsValue = rawVal ? dayjs(rawVal) : null;
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DesktopDatePicker
          value={dayjsValue?.isValid() ? dayjsValue : null}
          onChange={(newValue) => {
            const formatted = newValue?.isValid()
              ? newValue.format("YYYY-MM-DD")
              : "";
            handleGroupDetailsChange(item.field, formatted);
          }}
          format="MM/DD/YYYY"
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              placeholder: "MM/DD/YYYY",
              variant: "standard",
              InputProps: {
                disableUnderline: true,
              },
              sx: {
                "& .MuiInputBase-root": {
                  fontSize: "13.5px",
                  fontWeight: 500,
                  color: "#221E15",
                  fontFamily: "inherit",
                },
                "& .MuiInputBase-input": {
                  padding: 0,
                  height: "auto",
                },
                "& .MuiIconButton-root": {
                  padding: "2px",
                  color: "#403C6F",
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    );
  }

  return (
    <Box
      component="input"
      type={item.type || "text"}
      value={item.displayValue || groupDetails[item.field] || ""}
      onChange={(e) => handleGroupDetailsChange(item.field, e.target.value)}
      placeholder={item.placeholder || ""}
      sx={{
        border: "none",
        outline: "none",
        width: "100%",
        background: "transparent",
        fontFamily: "inherit",
        fontWeight: 500,
        fontSize: "13.5px",
        color: "#221E15",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
        "&::placeholder": {
          color: "#999893",
          opacity: 1,
          fontFamily: "inherit",
          fontWeight: 500,
          fontSize: "13.5px",
        },
      }}
    />
  );
};

const getTooltipTitle = (item, groupDetails, isFocused) => {
  if (item.field === "pcaDirective") {
    return groupDetails[item.field].length > 0 && !isFocused ? (
      <Box
        sx={{
          maxHeight: "200px",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {item.displayValue || groupDetails[item.field]}
      </Box>
    ) : null;
  }
  return item.displayValue || groupDetails[item.field] || "";
};

const GroupDetailItem = ({
  item,
  initialData,
  groupDetails,
  dynamicOrgOptions,
  dynamicCarrierOptions,
  dynamicGroupOptions,
  handleGroupDetailsChange,
  contentEditableRef,
  handlePcaPaste,
  handlePcaDrop,
  handlePcaInput,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const isReadonly = determineReadonly(item, initialData, groupDetails);
  const currentOptions = getGroupDetailOptions(
    item.label,
    item.options,
    dynamicOrgOptions,
    dynamicCarrierOptions,
    dynamicGroupOptions,
  );
  const renderAsSelect = item.options && !isReadonly;

  const containerSx = {
    width: "100%",
    ...(item.gridColumn ? { gridColumn: item.gridColumn } : {}),
  };

  const backgroundColor = isReadonly ? "#F0F0F1" : "#FFFFFF";
  const isTextarea = item.type === "textarea";

  const boxSx = isTextarea
    ? {
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        px: "8px",
        py: "10px",
        gap: "8px",
        width: "100%",
        minHeight: "auto",
        background: backgroundColor,
        border: "1px solid #E9E9E8",
        borderRadius: "8px",
      }
    : {
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        px: "16px",
        py: 0,
        gap: "10px",
        width: "100%",
        minHeight: "32px",
        background: backgroundColor,
        border: "1px solid #E9E9E8",
        borderRadius: "8px",
      };

  return (
    <Box key={item.field} sx={containerSx}>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "1.5",
          color: "#403C6F",
          mb: "8px",
        }}
      >
        {item.label}
        {item.star && <span style={{ color: "red" }}>*</span>}
      </Typography>
      <Tooltip
        title={getTooltipTitle(item, groupDetails, isFocused)}
        placement="top"
        arrow
      >
        <Box sx={{ width: "100%" }}>
          {renderAsSelect ? (
            <FilterDropdown
              label={item.label}
              options={currentOptions}
              selected={groupDetails[item.field]}
              placeholder={item.placeholder}
              width="100%"
              onChange={(val) => handleGroupDetailsChange(item.field, val)}
            />
          ) : (
            <Box sx={boxSx}>
              <InputContent
                isReadonly={isReadonly}
                item={item}
                groupDetails={groupDetails}
                contentEditableRef={contentEditableRef}
                handlePcaInput={handlePcaInput}
                handlePcaPaste={handlePcaPaste}
                handlePcaDrop={handlePcaDrop}
                setIsFocused={setIsFocused}
                handleGroupDetailsChange={handleGroupDetailsChange}
              />
            </Box>
          )}
        </Box>
      </Tooltip>
    </Box>
  );
};

GroupDetailItem.propTypes = {
  item: PropTypes.object.isRequired,
  initialData: PropTypes.object,
  groupDetails: PropTypes.object.isRequired,
  dynamicOrgOptions: PropTypes.array.isRequired,
  dynamicCarrierOptions: PropTypes.array.isRequired,
  dynamicGroupOptions: PropTypes.array.isRequired,
  handleGroupDetailsChange: PropTypes.func.isRequired,
  contentEditableRef: PropTypes.object,
  handlePcaPaste: PropTypes.func,
  handlePcaDrop: PropTypes.func,
  handlePcaInput: PropTypes.func,
};

InputContent.propTypes = {
  isReadonly: PropTypes.bool.isRequired,
  item: PropTypes.object.isRequired,
  groupDetails: PropTypes.object.isRequired,
  contentEditableRef: PropTypes.object,
  handlePcaInput: PropTypes.func,
  handlePcaPaste: PropTypes.func,
  handlePcaDrop: PropTypes.func,
  setIsFocused: PropTypes.func.isRequired,
  handleGroupDetailsChange: PropTypes.func.isRequired,
};

const DrugListRenderer = ({ drugList }) => {
  if (!drugList || drugList.length === 0) {
    return (
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "13px",
          color: "rgba(27, 47, 63, 0.6)",
        }}
      >
        Select drug list
      </Typography>
    );
  }
  if (drugList.length <= 2) {
    return (
      <>
        {drugList.map((drug) => (
          <Chip
            key={drug}
            label={drug}
            size="small"
            sx={{
              background: "#F4EFFE",
              color: "#403C6F",
              fontWeight: 500,
              fontSize: "12px",
              borderRadius: "4px",
              height: "24px",
              maxWidth: "140px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          />
        ))}
      </>
    );
  }
  return (
    <>
      <Chip
        label={drugList[0]}
        size="small"
        sx={{
          background: "#F4EFFE",
          color: "#403C6F",
          fontWeight: 500,
          fontSize: "12px",
          borderRadius: "4px",
          height: "24px",
          maxWidth: "180px",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      />
      <Chip
        label={`+${drugList.length - 1}`}
        size="small"
        sx={{
          background: "#F4EFFE",
          color: "#403C6F",
          fontWeight: 500,
          fontSize: "12px",
          borderRadius: "4px",
          height: "24px",
        }}
      />
    </>
  );
};

DrugListRenderer.propTypes = {
  drugList: PropTypes.array,
};

const ProgramConfigRow = ({
  config,
  index,
  isMobile,
  anchorEls,
  handleSelectOpen,
  handleSelectClose,
  handleSelectChange,
  handleProgramConfigChange,
}) => {
  const isInternationalSourcing =
    config.program === "International Sourcing" ||
    config.program === "Copay Assist";
  return (
    <TableRow key={config.program} sx={{ height: "52px" }}>
      <TableCell
        sx={{
          borderBottom: "1px solid #E8E8E9",
          borderRight: "1px solid #E8E8E9",
          p: "6px 16px",
          width: "260px",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "3px",
            px: "10px",
            py: "6px",
            background: "#ECECF1",
            borderRadius: "94px",
            minHeight: "24px",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            <ProgramIcon name={config.program} iconColor="#403C6F" />
          </Box>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "12px",
              lineHeight: "1.2",
              color: "#403C6F",
              wordBreak: "break-word",
            }}
          >
            {config.program}
          </Typography>
        </Box>
      </TableCell>

      <TableCell
        sx={{
          borderBottom: "1px solid #E8E8E9",
          borderRight: "1px solid #E8E8E9",
          p: "0 10px",
          width: "100px",
          height: "100%",
        }}
      >
        <Box
          onClick={(e) => {
            e.stopPropagation();
            handleSelectOpen(`program.${index}.status`, e);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            px: "12px",
            height: "32px",
            width: "100%",
            background: "#FFFFFF",
            border: "1px solid #E8E8E9",
            borderRadius: "4px",
            cursor: "pointer",
            "&:hover": {
              borderColor: "#403C6F",
            },
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontWeight: 400,
              fontSize: "14px",
              color: "#1B2F3F",
            }}
          >
            {config.status}
          </Typography>
          <ExpandMoreIcon sx={{ fontSize: 20, color: "#6E6D92" }} />
        </Box>
        <CommonSelect
          id={`status-select-${index}`}
          open={Boolean(anchorEls[`program.${index}.status`])}
          anchorEl={anchorEls[`program.${index}.status`]}
          onClose={() => handleSelectClose(`program.${index}.status`)}
          noteTypes={STATUS_OPTIONS}
          selectedTypes={[config.status]}
          handleTypeToggle={(val) =>
            handleSelectChange(`program.${index}.status`, val)
          }
          width={110}
          isModal={true}
        />
      </TableCell>

      <TableCell
        sx={{
          borderBottom: "1px solid #E8E8E9",
          borderRight: "1px solid #E8E8E9",
          p: "8px 10px",
        }}
      >
        <Box
          onClick={(e) => {
            if (config.status === "No") return;
            e.stopPropagation();
            handleSelectOpen(`program.${index}.drugList`, e);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            px: "12px",
            width: "100%",
            minHeight: "32px",
            background: config.status === "No" ? "#F0F0F1" : "#FFFFFF",
            border: "1px solid #E8E8E9",
            borderRadius: "4px",
            cursor: config.status === "No" ? "not-allowed" : "pointer",
            py: "4px",
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexWrap: "nowrap",
              gap: "4px",
              overflow: "hidden",
            }}
          >
            <DrugListRenderer drugList={config.drugList} />
          </Box>
          <ExpandMoreIcon sx={{ fontSize: 20, color: "#6E6D92" }} />
        </Box>
        <CommonSelect
          id={`druglist-select-${index}`}
          open={Boolean(anchorEls[`program.${index}.drugList`])}
          anchorEl={anchorEls[`program.${index}.drugList`]}
          onClose={() => handleSelectClose(`program.${index}.drugList`)}
          noteTypes={
            config.originalPc?.drugListConfigs?.map((d) => d.drugListName) ||
            PROGRAM_DRUG_LIST_MAP[config.program] ||
            []
          }
          selectedTypes={config.drugList || []}
          handleTypeToggle={(val) =>
            handleSelectChange(`program.${index}.drugList`, val)
          }
          multiple={true}
          width={isMobile ? 200 : 320}
          handleSelectAll={() => {
            const allOptions =
              config.originalPc?.drugListConfigs?.map((d) => d.drugListName) ||
              PROGRAM_DRUG_LIST_MAP[config.program] ||
              [];
            const isAllSelected = config.drugList.length === allOptions.length;
            handleProgramConfigChange(
              index,
              "drugList",
              isAllSelected ? [] : allOptions,
            );
          }}
          isModal={true}
        />
      </TableCell>

      <TableCell
        sx={{
          borderBottom: "1px solid #E8E8E9",
          p: "4px 8px",
          width: "150px",
          minWidth: "150px",
        }}
      >
        {isInternationalSourcing ? (
          <>
            <Box
              onClick={(e) => {
                if (config.status === "No") return;
                e.stopPropagation();
                handleSelectOpen(`program.${index}.vendor`, e);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                px: "12px",
                width: "100%",
                height: "32px",
                background: config.status === "No" ? "#F0F0F1" : "#FFFFFF",
                border: "1px solid #E8E8E9",
                borderRadius: "4px",
                cursor: config.status === "No" ? "not-allowed" : "pointer",
                py: "2px",
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontWeight: 500,
                  fontSize: "13px",
                  color:
                    config.status === "No" || !config.vendor
                      ? "rgba(27, 47, 63, 0.6)"
                      : "#1B2F3F",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {config.vendor ||
                  (isInternationalSourcing ? "Select Vendor" : "-")}
              </Typography>
              <ExpandMoreIcon sx={{ fontSize: 20, color: "#6E6D92" }} />
            </Box>
            <CommonSelect
              id={`vendor-select-${index}`}
              open={Boolean(anchorEls[`program.${index}.vendor`])}
              anchorEl={anchorEls[`program.${index}.vendor`]}
              onClose={() => handleSelectClose(`program.${index}.vendor`)}
              noteTypes={
                config.originalPc?.vendorConfigs?.map((v) => v.vendorName) ||
                VENDOR_OPTIONS
              }
              selectedTypes={[config.vendor]}
              handleTypeToggle={(val) =>
                handleSelectChange(`program.${index}.vendor`, val)
              }
              isModal={true}
              width={180}
            />
          </>
        ) : (
          <Typography
            sx={{
              px: "8px",
              fontWeight: 500,
              fontSize: "14px",
              color: "#333",
            }}
          >
            -
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

ProgramConfigRow.propTypes = {
  config: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isMobile: PropTypes.bool.isRequired,
  anchorEls: PropTypes.object.isRequired,
  handleSelectOpen: PropTypes.func.isRequired,
  handleSelectClose: PropTypes.func.isRequired,
  handleSelectChange: PropTypes.func.isRequired,
  handleProgramConfigChange: PropTypes.func.isRequired,
};

const useCagConfiguration = ({
  open,
  initialData,
  showLoading,
  hideLoading,
  onSave,
}) => {
  const dispatch = useDispatch();
  const [groupDetails, setGroupDetails] = useState(createInitialGroupDetails);
  const [dynamicOrgOptions, setDynamicOrgOptions] = useState([]);
  const [dynamicCarrierOptions, setDynamicCarrierOptions] = useState([]);
  const [dynamicGroupOptions, setDynamicGroupOptions] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [fullDetail, setFullDetail] = useState(null);
  const [programConfig, setProgramConfig] = useState([]);
  const [anchorEls, setAnchorEls] = useState({});
  const [initialComparableState, setInitialComparableState] = useState(null);
  const [apiError, setApiError] = useState("");

  const contentEditableRef = useRef(null);

  // Sync contentEditable ref with PCA directive value
  useEffect(() => {
    if (
      contentEditableRef.current &&
      contentEditableRef.current.textContent !== groupDetails.pcaDirective
    ) {
      contentEditableRef.current.textContent = groupDetails.pcaDirective || "";
    }
  }, [groupDetails.pcaDirective]);

  // PCA Directive event handlers
  const handlePcaPaste = useCallback((e) => {
    const items = e.clipboardData?.items || [];
    const files = e.clipboardData?.files || [];
    const hasImage =
      Array.from(items).some((i) => i.type?.startsWith("image/")) ||
      Array.from(files).some((f) => f.type?.startsWith("image/"));
    if (hasImage) {
      e.preventDefault();
    } else {
      const text = e.clipboardData?.getData("text/plain") || "";
      if (text) {
        e.preventDefault();
        const sel = globalThis.getSelection?.();
        if (sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          range.deleteContents();
          range.insertNode(document.createTextNode(text));
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
        }
        const newText = e.currentTarget.textContent || "";
        handleGroupDetailsChange("pcaDirective", newText);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePcaDrop = useCallback((e) => {
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      e.preventDefault();
    }
  }, []);

  const handlePcaInput = useCallback((e) => {
    handleGroupDetailsChange("pcaDirective", e.currentTarget.textContent);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetModalState = useCallback(() => {
    setGroupDetails(createInitialGroupDetails());
    setDynamicOrgOptions([]);
    setDynamicCarrierOptions([]);
    setDynamicGroupOptions([]);
    setHierarchy([]);
    setFullDetail(null);
    setProgramConfig([]);
    setAnchorEls({});
    setInitialComparableState(null);
    setApiError("");
  }, []);

  const loadCagOptions = useCallback(
    async (field, filters) => {
      try {
        const items = await fetchOptionsForField(field, filters, hierarchy);
        if (field === "org") setDynamicOrgOptions(items);
        else if (field === "carrier") setDynamicCarrierOptions(items);
        else if (field === "group") setDynamicGroupOptions(items);
      } catch (err) {
        console.error(`Failed to fetch ${field} options`, err);
      }
    },
    [hierarchy],
  );

  useEffect(() => {
    if (open) {
      resetModalState();
      if (initialData) {
        const initialPrograms =
          initialData?.cag?.programs || initialData?.programs || [];
        if (isOnlyCgPlusActive(initialPrograms)) {
          dispatch(setCgPlus(true));
        } else {
          dispatch(setCgPlus(false));
        }
      } else {
        dispatch(setCgPlus(false));
      }

      const fetchDetail = async () => {
        const cagId = initialData?.cag?.id || initialData?.id;
        try {
          showLoading();
          let detail;
          let hierarchyData = [];

          if (initialData) {
            const res = await getCagConfiguration(cagId);
            detail = res.data;
          } else {
            const [res, hierarchyRes] = await Promise.all([
              getCagConfiguration(cagId),
              fetchCagFilterOptions(),
            ]);
            detail = res.data;
            hierarchyData = hierarchyRes.data || [];
            setHierarchy(hierarchyData);
          }

          setFullDetail(detail);

          const cag = detail.cagDetail || {};
          const orgMatch = initialData
            ? null
            : hierarchyData.find((o) => o.orgCode === cag.orgCode);
          let orgName = "";
          if (initialData) {
            orgName = cag.org || cag.orgCode || "";
          } else if (orgMatch) {
            orgName = orgMatch.orgName;
          } else {
            orgName = cag.orgCode || "";
          }

          const initialDetails = {
            org: cag.orgCode || "",
            orgName,
            carrierId: cag.carrierCode || "",
            carrierName: cag.carrier || "",
            groupId: cag.groupNumber || "",
            groupName: cag.groupName || "",
            activeLives: cag.lives?.toString() || "0",
            groupStartDate: cag.groupStartDate || "",
            groupEndDate: cag.groupEndDate || "",
            careGuideEffectiveDate: cag.careGuideEffectiveDate || "",
            pcaDirectiveEnabled: cag.pcaDirectiveEnabled ?? false,
            pcaDirective: cag.pcaDirective || "",
            parentAccountName: cag.parentAccountName || "",
          };

          setGroupDetails(initialDetails);
          const rawConfig = (detail.programConfigs || []).map(mapProgramConfig);
          setProgramConfig(rawConfig);
          if (initialData) {
            dispatch(setCgPlus(isOnlyCgPlusActive(rawConfig)));
          }
          setInitialComparableState(
            createComparableModalState(initialDetails, rawConfig),
          );
        } catch (err) {
          console.error("Failed to fetch detail/template", err);
          if (!initialData) {
            setGroupDetails(createInitialGroupDetails());
            setProgramConfig(
              PROGRAMS.map((program, idx) => ({
                programId: idx + 1,
                program,
                status: "No",
                drugList: [],
                vendor:
                  program === "International Sourcing" ||
                  program === "Copay Assist"
                    ? ""
                    : "-",
              })),
            );
          }
        } finally {
          hideLoading();
        }
      };
      void fetchDetail();
    }
  }, [dispatch, hideLoading, initialData, open, resetModalState, showLoading]);

  useEffect(() => {
    if (!open) {
      resetModalState();
      dispatch(setCgPlus(false));
    }
  }, [dispatch, open, resetModalState]);

  useEffect(() => {
    if (open) loadCagOptions("org", {});
  }, [open, loadCagOptions]);

  useEffect(() => {
    if (open && groupDetails.org) {
      loadCagOptions("carrier", { org: [groupDetails.org] });
    } else {
      setDynamicCarrierOptions([]);
    }
  }, [groupDetails.org, open, loadCagOptions]);

  useEffect(() => {
    if (open && groupDetails.org && groupDetails.carrierId) {
      loadCagOptions("group", {
        org: [groupDetails.org],
        carrier: [groupDetails.carrierId],
      });
    } else {
      setDynamicGroupOptions([]);
    }
  }, [groupDetails.org, groupDetails.carrierId, open, loadCagOptions]);

  const findMatch = (options, val) =>
    options.find(
      (opt) => String(opt.id) === String(val) || opt.displayValue === val,
    );

  const handleGroupDetailsChange = (field, value) => {
    setGroupDetails((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === "orgName" || field === "org") {
        const match = findMatch(dynamicOrgOptions, value);
        if (match) {
          updated.org = match.id;
          updated.orgName = match.displayValue;
        }
        updated.carrierId = "";
        updated.carrierName = "";
        updated.groupId = "";
        updated.groupName = "";
        updated.activeLives = "0";
        updated.pcaDirective = "";
      } else if (field === "carrierName" || field === "carrierId") {
        const match = findMatch(dynamicCarrierOptions, value);
        if (match) {
          updated.carrierId = match.id;
          updated.carrierName = match.displayValue;
        }
        updated.groupId = "";
        updated.groupName = "";
        updated.activeLives = "0";
        updated.pcaDirective = "";
      } else if (field === "groupName" || field === "groupId") {
        const match = findMatch(dynamicGroupOptions, value);
        if (match) {
          updated.groupId = match.id;
          updated.groupName = match.displayValue;
          updated.activeLives = (match.activeLives || 0).toString();
          updated.groupStartDate = match.groupStartDate || "";
          updated.groupEndDate = match.groupEndDate || "";
          updated.pcaDirective = "";
        }
      }
      return updated;
    });
  };

  const handleProgramConfigChange = (index, field, value) => {
    setProgramConfig((prev) => {
      const updated = [...prev];
      const prog = updated[index];
      const defVendor =
        prog.program === "International Sourcing" ||
        prog.program === "Copay Assist"
          ? ""
          : "-";

      if (field === "status") {
        updated[index] = {
          ...prog,
          status: value,
          drugList: value === "No" ? [] : prog.drugList,
          vendor: defVendor,
        };
      } else {
        updated[index] = { ...prog, [field]: value };
      }
      return updated;
    });
  };

  const handleSelectOpen = (field, event) => {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget;
    setAnchorEls((prev) => ({
      ...Object.keys(prev).reduce(
        (next, key) => ({ ...next, [key]: key === field ? target : null }),
        {},
      ),
      [field]: target,
    }));
  };

  const handleSelectClose = (field) => {
    setAnchorEls((prev) => ({ ...prev, [field]: null }));
  };

  const handleSelectChange = (field, value) => {
    if (field.startsWith("groupDetails.")) {
      handleGroupDetailsChange(field.replace("groupDetails.", ""), value);
    } else if (field.startsWith("program.")) {
      const parts = field.split(".");
      const idx = Number.parseInt(parts[1], 10);
      const fld = parts[2];

      if (fld === "drugList") {
        const current = programConfig[idx].drugList || [];
        const updated = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value];
        handleProgramConfigChange(idx, fld, updated);
        return;
      }
      handleProgramConfigChange(idx, fld, value);
    }
    handleSelectClose(field);
  };

  const handleSave = async () => {
    const payload = {
      cagDetail: {
        cagId: fullDetail?.cagDetail?.cagId || null,
        org: groupDetails.orgName,
        orgCode: groupDetails.org,
        carrier: groupDetails.carrierName,
        carrierCode: groupDetails.carrierId,
        groupName: groupDetails.groupName,
        groupNumber: groupDetails.groupId,
        lives: Number.parseInt(groupDetails.activeLives, 10) || 0,
        groupStartDate: formatDateForBackend(groupDetails.groupStartDate),
        groupEndDate: formatDateForBackend(groupDetails.groupEndDate),
        careGuideEffectiveDate: formatDateForBackend(
          groupDetails.careGuideEffectiveDate,
        ),
        pcaDirectiveEnabled: Boolean(groupDetails.pcaDirectiveEnabled),
        pcaDirective: groupDetails.pcaDirective || "",
        parentAccountName: groupDetails.parentAccountName || "",
      },
      programConfigs: programConfig.map((pc) => {
        const opc = pc.originalPc || {};
        return {
          programId: pc.programId,
          programName: pc.program,
          enabled: pc.status === "Yes",
          status: pc.status === "Yes",
          vendorConfigs: (opc.vendorConfigs || []).map((v) => ({
            ...v,
            enabled: v.vendorName === pc.vendor,
          })),
          drugListConfigs: (opc.drugListConfigs || []).map((d) => ({
            ...d,
            enabled: pc.drugList.includes(d.drugListName),
          })),
        };
      }),
    };
    const result = await onSave(payload);
    if (result?.success === false) {
      setApiError(
        result.error?.response?.data?.message ||
          result.error?.message ||
          "An error occurred while saving.",
      );
    } else {
      resetModalState();
    }
  };

  const hasEditChanges = useMemo(() => {
    if (!initialData) return true;
    if (!initialComparableState) return false;
    return (
      JSON.stringify(initialComparableState) !==
      JSON.stringify(createComparableModalState(groupDetails, programConfig))
    );
  }, [initialData, initialComparableState, groupDetails, programConfig]);

  const isSaveEnabled =
    groupDetails.org &&
    groupDetails.carrierId &&
    groupDetails.groupId &&
    programConfig.some((pc) => pc.status === "Yes") &&
    hasEditChanges;

  return {
    groupDetails,
    programConfig,
    anchorEls,
    dynamicOrgOptions,
    dynamicCarrierOptions,
    dynamicGroupOptions,
    apiError,
    isSaveEnabled,
    contentEditableRef,
    handlePcaPaste,
    handlePcaDrop,
    handlePcaInput,
    handleModalClose: () => {
      resetModalState();
      return true;
    },
    handleSelectOpen,
    handleSelectClose,
    handleSelectChange,
    handleGroupDetailsChange,
    handleProgramConfigChange,
    handleSave,
  };
};

const ModalHeader = ({ isMobile, initialData, onClose }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: isMobile ? "flex-start" : "center",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? "8px" : "24px",
        flex: 1,
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: isMobile ? "18px" : "20px",
          color: "#10100F",
          whiteSpace: "nowrap",
        }}
      >
        {initialData ? "Edit Configuration" : "Add New Configuration"}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          px: "12px",
          py: "8px",
          background: "#FFF3F3",
          boxShadow: "0px 0px 2px rgba(0, 0, 0, 0.25)",
          borderRadius: "8px",
          height: isMobile ? "auto" : "30px",
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 15, color: "#6A6A6A" }} />
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: isMobile ? 11 : 13,
            color: "#6A6A6A",
          }}
        >
          Changes to configurations may take up to 30 minutes to take effect
        </Typography>
      </Box>
    </Box>
    <IconButton onClick={onClose} sx={{ color: "#10100F" }}>
      <CloseIcon />
    </IconButton>
  </Box>
);

const GroupDetailsSection = ({
  groupDetails,
  initialData,
  dynamicOrgOptions,
  dynamicCarrierOptions,
  dynamicGroupOptions,
  handleGroupDetailsChange,
  apiError,
  contentEditableRef,
  handlePcaPaste,
  handlePcaDrop,
  handlePcaInput,
}) => (
  <Box
    sx={{
      backgroundColor: "#FFFFFF",
      borderRadius: "16px",
      border: "1px solid #E9E9E8",
      p: "20px",
      display: "flex",
      flexDirection: "column",
      gap: "24px",
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "17px",
          color: "#10100F",
          whiteSpace: "nowrap",
        }}
      >
        Group Details
      </Typography>
      {apiError && (
        <Typography
          sx={{
            color: "#d32f2f",
            fontSize: "14px",
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          {apiError}
        </Typography>
      )}
    </Box>

    <Box
      sx={{
        backgroundColor: "#F7F8FB",
        borderRadius: "10px",
        p: "10px",
        display: "flex",
        flexDirection: "column",
        gap: "32px",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
          gap: "16px",
          width: "100%",
        }}
      >
        {[
          {
            label: "ORG",
            field: "org",
            displayValue: groupDetails.orgName,
            options: ORG_OPTIONS,
            placeholder: "Select ORG",
            star: true,
          },
          {
            label: "Carrier Name",
            field: "carrierId",
            displayValue: groupDetails.carrierName,
            options: CARRIER_NAME_OPTIONS,
            placeholder: "Select Carrier Name",
            star: true,
          },
          {
            label: "Carrier ID",
            field: "carrierId",
            options: CARRIER_OPTIONS,
            placeholder: "Select Carrier ID",
            star: true,
          },
          {
            label: "Parent Account",
            field: "parentAccountName",
            type: "text",
            placeholder: "Enter Parent Account",
            star: false,
          },
          {
            label: "Group Name",
            field: "groupId",
            displayValue: groupDetails.groupName,
            options: GROUP_NAME_OPTIONS,
            placeholder: "Select Group Name",
            star: true,
          },
          {
            label: "Group Number",
            field: "groupId",
            options: GROUP_ID_OPTIONS,
            placeholder: "Select Group Number",
            star: true,
          },
          {
            label: "Group Start Date",
            field: "groupStartDate",
            star: false,
            isDate: true,
          },
          {
            label: "Group End Date",
            field: "groupEndDate",
            star: false,
            isDate: true,
          },
          {
            label: "Care Guide Effective Date",
            field: "careGuideEffectiveDate",
            star: false,
            isDate: true,
          },
          {
            label: "Active Lives",
            field: "activeLives",
            type: "number",
            star: false,
          },
          {
            label: "PCA Directive",
            field: "pcaDirective",
            type: "textarea",
            star: false,
            placeholder: "PCA directive details",
            gridColumn: "1 / -1",
          },
        ].map((item) => (
          <GroupDetailItem
            key={item.field}
            item={item}
            initialData={initialData}
            groupDetails={groupDetails}
            dynamicOrgOptions={dynamicOrgOptions}
            dynamicCarrierOptions={dynamicCarrierOptions}
            dynamicGroupOptions={dynamicGroupOptions}
            handleGroupDetailsChange={handleGroupDetailsChange}
            contentEditableRef={contentEditableRef}
            handlePcaPaste={handlePcaPaste}
            handlePcaDrop={handlePcaDrop}
            handlePcaInput={handlePcaInput}
          />
        ))}
      </Box>
    </Box>
  </Box>
);

const ProgramConfigurationSection = ({
  programConfig,
  isMobile,
  anchorEls,
  handleSelectOpen,
  handleSelectClose,
  handleSelectChange,
  handleProgramConfigChange,
}) => {
  const isCgPlusOn = useSelector((state) => state.toggles.cgPlus);
  const dispatch = useDispatch();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Typography
          sx={{ fontWeight: 600, fontSize: "17px", color: "#10100F" }}
        >
          Program Configuration
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            bgcolor: "#fff",
            color: "text.primary",
            textTransform: "none",
            padding: "4px 12px",
            borderRadius: "20px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: isCgPlusOn ? 600 : 500,
              color: isCgPlusOn ? "#403C6F" : "#7F7C9F",
            }}
          >
            CG+ programs
          </Typography>
          <ToggleButton
            isOn={isCgPlusOn}
            onChange={() => dispatch(toggleCgPlus())}
            label=""
            onLabel=""
            offLabel=""
          />
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          borderRadius: "16px",
          border: "1px solid #E8E8E9",
          mb: 2,
        }}
      >
        <TableContainer>
          <Table sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                {[
                  { label: "Programs", width: "230px", radius: "16px 0 0 0" },
                  { label: "Status", width: "100px" },
                  { label: "Drug list", width: "auto" },
                  { label: "Vendor", width: "150px", radius: "0 16px 0 0" },
                ].map((col) => (
                  <TableCell
                    key={col.label}
                    sx={{
                      minWidth: col.width,
                      background: "#CFCEDA",
                      padding: "10px 16px",
                      borderRadius: col.radius || 0,
                      border: "none",
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#403C6F",
                      borderRight:
                        col.label === "Vendor" ? "" : "1px solid white",
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {programConfig.map((config, index) => {
                const isCg = isProgramCgPlus(config);
                const shouldShow = isCgPlusOn ? isCg : !isCg;
                if (!shouldShow) return null;
                return (
                  <ProgramConfigRow
                    key={config.program}
                    config={config}
                    index={index}
                    isMobile={isMobile}
                    anchorEls={anchorEls}
                    handleSelectOpen={handleSelectOpen}
                    handleSelectClose={handleSelectClose}
                    handleSelectChange={handleSelectChange}
                    handleProgramConfigChange={handleProgramConfigChange}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

const ModalActions = ({
  isMobile,
  isSaveEnabled,
  onCancel,
  onSave,
  isEdit,
}) => (
  <DialogActions
    sx={{
      p: 0,
      justifyContent: "flex-end",
      gap: "10px",
      mt: "-1px",
      flexDirection: isMobile ? "column" : "row",
      alignItems: isMobile ? "stretch" : "center",
    }}
  >
    <Button
      onClick={onCancel}
      sx={{
        width: isMobile ? "100%" : "86px",
        height: "40px",
        background: "#FFFFFF",
        border: "1px solid #9F9DB6",
        borderRadius: "52px",
        fontWeight: 600,
        fontSize: "14px",
        textTransform: "none",
        color: "#403C6F",
      }}
    >
      Cancel
    </Button>
    <Button
      disabled={!isSaveEnabled}
      onClick={onSave}
      sx={{
        width: isMobile ? "100%" : "auto",
        minWidth: isMobile ? "100%" : "181px",
        px: "24px",
        height: "40px",
        background: isSaveEnabled ? "#403C6F" : "#BDBDBD",
        borderRadius: "52px",
        fontWeight: 600,
        fontSize: "14px",
        textTransform: "none",
        color: "#FFFFFF",
        "&:hover": { background: isSaveEnabled ? "#2F2C46" : "#BDBDBD" },
      }}
    >
      {isEdit ? "Save Configurations" : "Add Configuration"}
    </Button>
  </DialogActions>
);

ModalHeader.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  initialData: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

GroupDetailsSection.propTypes = {
  groupDetails: PropTypes.object.isRequired,
  initialData: PropTypes.object,
  dynamicOrgOptions: PropTypes.array.isRequired,
  dynamicCarrierOptions: PropTypes.array.isRequired,
  dynamicGroupOptions: PropTypes.array.isRequired,
  handleGroupDetailsChange: PropTypes.func.isRequired,
  apiError: PropTypes.string,
  contentEditableRef: PropTypes.object,
  handlePcaPaste: PropTypes.func,
  handlePcaDrop: PropTypes.func,
  handlePcaInput: PropTypes.func,
};

ProgramConfigurationSection.propTypes = {
  programConfig: PropTypes.array.isRequired,
  isMobile: PropTypes.bool.isRequired,
  anchorEls: PropTypes.object.isRequired,
  handleSelectOpen: PropTypes.func.isRequired,
  handleSelectClose: PropTypes.func.isRequired,
  handleSelectChange: PropTypes.func.isRequired,
  handleProgramConfigChange: PropTypes.func.isRequired,
};

ModalActions.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  isSaveEnabled: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
};

const EditConfigurationModal = ({
  open,
  onClose: onCloseModals,
  onSave,
  initialData,
}) => {
  const dispatch = useDispatch();
  const onClose = useCallback(() => {
    onCloseModals();
    dispatch(setCgPlus(false));
  }, [dispatch, onCloseModals]);
  const { showLoading, hideLoading } = useLoading();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const {
    groupDetails,
    programConfig,
    anchorEls,
    dynamicOrgOptions,
    dynamicCarrierOptions,
    dynamicGroupOptions,
    apiError,
    isSaveEnabled,
    contentEditableRef,
    handlePcaPaste,
    handlePcaDrop,
    handlePcaInput,
    handleModalClose,
    handleSelectOpen,
    handleSelectClose,
    handleSelectChange,
    handleGroupDetailsChange,
    handleProgramConfigChange,
    handleSave,
  } = useCagConfiguration({
    open,
    initialData,
    showLoading,
    hideLoading,
    onSave,
  });

  const onCloseModal = () => {
    if (handleModalClose()) onClose();
  };

  const paperWidth = (() => {
    if (isMobile) return "95%";
    if (isTablet) return "90%";
    return "70%";
  })();

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onCloseModal}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: paperWidth,
            maxWidth: "none",
            borderRadius: "16px",
            backgroundColor: "#F7F8FB",
            display: "flex",
            flexDirection: "column",
            p: isMobile ? "12px" : "20px",
            gap: isMobile ? "12px" : "16px",
            ml: isTablet ? "0" : "17%",
            m: isMobile ? 1 : 2,
          },
        },
      }}
    >
      <GlobalStyles
        styles={{
          '[id^="druglist-select-"] .MuiMenuItem-root:not(:first-of-type)': {
            backgroundColor: "transparent !important",
          },
          '[id^="druglist-select-"] .MuiMenuItem-root:not(:first-of-type):hover':
            {
              backgroundColor: "rgba(255,255,255,0.08) !important",
            },
          '[id^="status-select-"] .MuiMenuItem-root, [id^="vendor-select-"] .MuiMenuItem-root':
            {
              backgroundColor: "transparent !important",
            },
          '[id^="status-select-"] .MuiMenuItem-root:hover, [id^="vendor-select-"] .MuiMenuItem-root:hover':
            {
              backgroundColor: "rgba(255,255,255,0.08) !important",
            },
        }}
      />
      <ModalHeader
        isMobile={isMobile}
        initialData={initialData}
        onClose={onCloseModal}
      />

      <DialogContent
        sx={{
          p: 0,
          overflowY: "auto",
          maxHeight: "85vh",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E9E9E8",
            p: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <GroupDetailsSection
            groupDetails={groupDetails}
            initialData={initialData}
            dynamicOrgOptions={dynamicOrgOptions}
            dynamicCarrierOptions={dynamicCarrierOptions}
            dynamicGroupOptions={dynamicGroupOptions}
            handleGroupDetailsChange={handleGroupDetailsChange}
            apiError={apiError}
            contentEditableRef={contentEditableRef}
            handlePcaPaste={handlePcaPaste}
            handlePcaDrop={handlePcaDrop}
            handlePcaInput={handlePcaInput}
          />

          <ProgramConfigurationSection
            programConfig={programConfig}
            isMobile={isMobile}
            anchorEls={anchorEls}
            handleSelectOpen={handleSelectOpen}
            handleSelectClose={handleSelectClose}
            handleSelectChange={handleSelectChange}
            handleProgramConfigChange={handleProgramConfigChange}
          />
        </Box>
      </DialogContent>

      <ModalActions
        isMobile={isMobile}
        isSaveEnabled={isSaveEnabled}
        onCancel={onCloseModal}
        onSave={handleSave}
        isEdit={Boolean(initialData)}
      />
    </Dialog>
  );
};

EditConfigurationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialData: PropTypes.object,
};

export { isProgramCgPlus, isProgramActive, isOnlyCgPlusActive };
export default EditConfigurationModal;
